'use client';

import {
  BlogCarouselItem,
  SectionBlogCarousel,
} from '@sbercloud/uikit-product-site-section';
import { Item } from '@snack-uikit/breadcrumbs';
import { useRouter } from 'next/navigation';
import Script from 'next/script';
import { MouseEvent } from 'react';
import React, { useMemo } from 'react';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useAsPath } from 'src/hooks/useAsPath';
import { useLayoutType } from 'src/hooks/useLayoutType';
import { Layout } from 'src/layout';
import { ArticleData } from 'src/types';
import { ArticleContentBlockTypes, TextBlock } from 'src/types/articleCmsTypes';
import { formatDate } from 'src/utils/formatDate';
import { genStringForUrl } from 'src/utils/genStringForUrl';
import { getTimeToRead } from 'src/utils/getTimeToRead';

import {
  ArticleLink,
  ArticleLinks,
  BlogContent,
  BlogProduct,
  FooterArticle,
  HeaderArticle,
  Products,
  SectionArticle,
} from './components';

export type BlogPageProps = {
  blog: ArticleData;
};

export function BlogPage({ blog }: BlogPageProps) {
  const asPath = useAsPath();
  const router = useRouter();
  const { clickAnalytics } = useAnalytics();

  const layoutType = useLayoutType();

  const handleBreadcrumbsClick =
    (slug: string, label: string) =>
    (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      e.preventDefault();

      clickAnalytics({
        action: slug,
        clickContent: label,
        clickElement: 'breadcrumb',
        clickZone: 'body',
        clickZoneCategory: 'site-body',
        transitionType: 'inside-link',
        uniqueId: `article-breadcrumb-${label}`,
        transitionTypeMetrics: 'internal',
        transitionUrl: slug,
      });

      router.push(slug);
    };

  const breadcrumbs: Item[] = [
    {
      id: 'main',
      label: 'Главная',
      href: '/',
      onClick: handleBreadcrumbsClick('/', 'Главная'),
    },
    {
      id: 'blog',
      label: 'Блог',
      href: '/blog',
      onClick: handleBreadcrumbsClick('/blog', 'Блог'),
    },
    {
      id: blog.slug,
      label: blog.title,
    },
  ];

  const [author] = blog.authors || [];
  const timeToRead = blog.timeToRead || getTimeToRead(blog.content);

  const articleProducts = useMemo(() => {
    if (!blog.products?.length) {
      return null;
    }

    const products: BlogProduct[] = blog.products.map((product) => ({
      name: product.title,
      icon: product.icon,
      onClick: () => {
        clickAnalytics({
          action: `/products/${product.slug}`,
          clickContent: product.title,
          clickElement: 'button',
          clickZone: 'body',
          clickZoneCategory: 'site-body',
          transitionType: 'inside-link',
          uniqueId: `article-products-${product.slug}`,
          transitionTypeMetrics: 'internal',
          transitionUrl: `/products/${product.slug}`,
        });

        return router.push(`/products/${product.slug}`);
      },
    }));

    return <Products products={products} />;
  }, [blog.products, clickAnalytics, router]);

  const links: ArticleLink[] = blog.content.blocks
    .filter((block): block is TextBlock =>
      Boolean(block.type === ArticleContentBlockTypes.TextBlock && block.title),
    )
    .map((block) => ({
      id: genStringForUrl(block.title || ''),
      text: block.title || '',
      onClick: (link) => {
        clickAnalytics({
          action: 'click',
          clickContent: link.text,
          clickElement: 'button',
          clickZone: 'body',
          clickZoneCategory: 'site-body',
          transitionType: 'inside-link',
          uniqueId: `article-anchor-link-${link.id}`,
        });
      },
    }));

  const carouselCards = useMemo(
    () =>
      blog.articlesRelated?.map(
        (article): BlogCarouselItem => ({
          date: formatDate(article.releaseDate),
          href: `/blog/${article.slug}`,
          img: article.cover,
          tag: { label: article.categoryTitle },
          title: article.title,
          time: article.timeToRead,
          titleTag: 'span',
          onClick: (e) => {
            e?.preventDefault();

            const link = `/blog/${article.slug}`;

            clickAnalytics({
              action: link,
              clickContent: article.title,
              clickElement: 'cardclick',
              clickZone: 'body',
              clickZoneCategory: 'site-body',
              transitionType: 'inside-link',
              uniqueId: `article-related-${article.slug}`,
              transitionTypeMetrics: 'internal',
              transitionUrl: link,
            });

            router.push(link);
          },
        }),
      ),
    [blog.articlesRelated, clickAnalytics, router],
  );

  const jsonBlog = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://cloud.ru/${asPath}`,
    },
    headline: blog?.title,
    image: '',
    author: {
      '@type': 'Person',
      name: blog?.authors
        ? `${blog.authors[0].name} ${blog.authors[0].secondName}`
        : '',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Cloud.ru',
      logo: {
        '@type': 'ImageObject',
        url: 'https://cloud.ru/favicons/logo-180.png',
      },
    },
    datePublished: blog.releaseDate,
    cover: blog.cover,
  };

  return (
    <Layout>
      <SectionArticle
        layoutType={layoutType}
        breadcrumbs={breadcrumbs}
        title={blog.title}
        description={blog.description}
      >
        <HeaderArticle
          layoutType={layoutType}
          articleTitle={blog.title}
          tag={blog.categoryTitle}
          image={blog.cover}
          author={
            author
              ? {
                  name: `${author.name} ${author.secondName}`,
                  position: author.position,
                  image: author.avatar,
                }
              : undefined
          }
          timeToRead={timeToRead}
          releaseDate={blog.releaseDate}
        />
        {articleProducts}
        {links.length > 0 ? (
          <ArticleLinks layoutType={layoutType} links={links} />
        ) : null}
        <BlogContent blocks={blog.content.blocks} />
        {articleProducts}
        <FooterArticle layoutType={layoutType} releaseDate={blog.releaseDate} />
      </SectionArticle>
      {carouselCards ? (
        <SectionBlogCarousel
          title="Вам может понравиться"
          layoutType={layoutType}
          items={carouselCards}
          backgroundColor="neutral-background"
          data-test-id="related-articles"
        />
      ) : null}
      <Script
        id="microblog"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonBlog) }}
      />
    </Layout>
  );
}
